"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import RichText from "payload-pms/RichText";
const styles = new Proxy({"TextCards":"TextCards-c3293","Card":"Card-c3293","CardTitle":"CardTitle-c3293","CardContent":"CardContent-c3293","AccordionItem":"AccordionItem-c3293","AccordionTrigger":"AccordionTrigger-c3293","AccordionTriggerActive":"AccordionTriggerActive-c3293","AccordionBodyInner":"AccordionBodyInner-c3293","Cta":"Cta-c3293"}, { get: (m, k) => (typeof k === "string" ? (m[k] ?? k) : m[k]) });

interface TextCard {
  title: string;
  content: any;
  ctaLabel?: string;
  ctaHref?: string;
}

interface TextCardsProps {
  block: {
    cards?: TextCard[];
  };
}

const FALLBACK_TEXTS: Record<string, string> = {
  "Types of Sensors":
    "Fuelled by our passion for addressing various challenges involving movement analysis, we are constantly exploring an ever widening range of sensor types without imposing limitations. Our expertise spans the entirety of motion recording technology, including both pre-built and personalized solutions. To mention a few examples, we have used tablet-and-pen configurations for capturing signatures; computer mice for monitoring user activity in continuous authentication solutions; heart rate sensors, eye trackers, wearable IMUs, and marker-based/markerless motion capture solutions for recording motion data in digital health applications.\n\nWe take into account each project's distinct requirements and choose the most appropriate sensor solution that balances ease of use with maximized data quality.",
  "Data Preprocessing":
    "Regardless of the sensor type used to capture motion data, it's essential to recognize that any measurement is inherently inaccurate. The nature of the noise present in the data provided rarely aligns with the manufacturers' technical specification. This issue could stem from the device's inherent limitations or deliberate modifications in the firmware that either smooth or distort the actual motion.\n\nFor example, when recording data with a mouse, angle snapping or mouse acceleration is a standard noise added by the computer. Additionally, typical artistic users prefer seeing smooth lines on the tablet screen, thereby necessitating smoothing. Despite these challenges, most motion analysis experts rely on standard data cleaning and smoothing algorithms to manage inaccurate and noisy raw data, but we believe that these standard approaches fall short. Although standard filters might display a smoother line on the screen, they may further distort the acceleration data of the cursor.\n\nAs such, we employ a reverse engineering approach, thoroughly examining each device, understanding the background and figuring out how to carry out device-specific data preprocessing in order to recreate the original movement as accurately as possible.",
  "Extracted Features":
    "Our analytical stack employs a proprietary universal feature space to represent multidimensional time series datasets. This feature space was initially created using the technical meta-language of handwriting analysis, but has since been adapted over the past 15 years to suit other domains as well. It relies heavily on the dynamical properties of movements to provide a detailed representation of the motor program patterns that underlie the data. The extracted motion features have been effectively used in various fields, such as signature verification, continuous mouse movement authentication for cybersecurity purposes, personality trait prediction, and medical applications.\n\nLeveraging this feature space provides us with a significant advantage over the prevalent use of standard physical features that lack deeper insight into the captured motion data.",
  "Feature Screening":
    "The selection of an optimal feature subset that effectively characterizes any movement data is pivotal for building efficient machine learning models. Given that our feature space consists of tens of thousands of features, it is critical to identify the most relevant ones for each specific task.\n\nBy working with a carefully selected, small subset of features, we can construct accurate prediction models even when only a limited amount of data is available. We have tried a wide range of well-known screening methods to accomplish this, but none of these methods yielded the desired results.\n\nConsequently, we have developed our own feature screener method in collaboration with the Wigner Research Centre within the Hungarian Academy of Sciences, which we call the Random Forest - based Multi - Round Screening Method (RFMS). The Julia package that implements RFMS is publicly available on GitHub. To benchmark our algorithm, we used our own dataset generator called BiometricBlender which is also publicly available.",
  "Prediction Models":
    "Rather than relying on basic data analysis methods or feeding vast amounts of data blindly into deep learning models, our approach is to thoroughly comprehend the problem and the data at hand, and then select the appropriate solution. We are experts in various sophisticated decision making tools and always choose the most suitable one for a given project. Our proprietary AI toolchain enables us to extract task-specific, meaningful features that capture the essence of movement, resulting in more explainable models that in turn often lead to new discoveries.",
  "Cross-domain Knowledge":
    "We are always keen to gain a deeper understanding of human movement, and we have gathered experience in motion analysis across a wide range of domains, such as: Signature verification, Cybersecurity, Bot detection, Continuous authentication, Gaming, Air traffic control, Athletics, Video-based walking recognition, Personality trait prediction, Neurological condition assessment, Emotional state and stress level evaluation, Biomedical signal analysis.\n\nThis means that we are equipped with valuable and transferable skills that can be leveraged in any task involving complex time series analysis.",
  "Scalable Solutions":
    "In contrast to many research-focused teams, we have the experience of delivering commercial solutions that can scale to meet the highest business requirements. One such example is our biometric signature authentication solution, which has been relied upon by major banks for numerous years.\n\nOur system is capable of analyzing millions of signatures per year and handling highly sensitive biometric data for millions of users, while adhering to strict security, GDPR and ISO standards. Our team is committed to comprehending each client's unique needs and providing solutions to challenges that may arise.",
  "Access to data recorded under laboratory conditions":
    "We are fortunate to have an ongoing partnership with the Hungarian National Institute of Clinical Neurosciences, where we provide the infrastructure for a comprehensive/dedicated Motion Lab, and we take charge of the motion data analysis tasks. This lab includes a 12-camera optical motion capture system, a treadmill, a wearable eye tracker, and tablet + pen workstations that can assess both gross and fine motor skills during assessments.\n\nWith these resources and support in place, we can efficiently collect and analyze motion data to gain insights that matter.",
};

const DEFAULT_CARDS_FULL: TextCard[] = [
  { title: "Types of Sensors", content: null },
  {
    title: "Data Preprocessing",
    content: null,
    ctaLabel: "Learn more",
    ctaHref: "#",
  },
  {
    title: "Extracted Features",
    content: null,
    ctaLabel: "Learn more",
    ctaHref: "#",
  },
  {
    title: "Feature Screening",
    content: null,
    ctaLabel: "Learn more",
    ctaHref: "#",
  },
  { title: "Prediction Models", content: null },
  { title: "Cross-domain Knowledge", content: null },
  { title: "Scalable Solutions", content: null },
  {
    title: "Access to data recorded under laboratory conditions",
    content: null,
  },
];

function CardBody({ card }: { card: TextCard }) {
  const fallbackText = FALLBACK_TEXTS[card.title];
  return (
    <>
      {card.content ? (
        <div className={styles.CardContent}>
          <RichText content={card.content} />
        </div>
      ) : fallbackText ? (
        <div className={styles.CardContent}>
          {fallbackText.split("\n\n").map((para, j) => (
            <p key={j}>{para}</p>
          ))}
        </div>
      ) : null}
      {card.ctaLabel && card.ctaHref && (
        <Link
          href={card.ctaHref}
          className={styles.Cta}
          target="_blank"
          rel="noopener noreferrer"
        >
          {card.ctaLabel}
        </Link>
      )}
    </>
  );
}

function AccordionPanel({ card, open }: { card: TextCard; open: boolean }) {
  return (
    <div
      className={styles.AccordionBody}
      style={{ overflow: "hidden", transition: "height 0.3s ease" }}
    >
      <div className={styles.AccordionBodyInner}>
        <CardBody card={card} />
      </div>
    </div>
  );
}

export default function TextCards({ block }: TextCardsProps) {
  const cards = block.cards?.length ? block.cards : DEFAULT_CARDS_FULL;
  const [accordionOpen, setAccordionOpen] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  if (isMobile) {
    return (
      <div className={styles.TextCards}>
        {cards.map((card, i) => (
          <div key={i} className={styles.AccordionItem}>
            <button
              className={
                i === accordionOpen
                  ? styles.AccordionTriggerActive
                  : styles.AccordionTrigger
              }
              onClick={() => setAccordionOpen(i === accordionOpen ? -1 : i)}
            >
              {card.title}
            </button>
            <AccordionPanel card={card} open={i === accordionOpen} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.TextCards}>
      {cards.map((card, i) => (
        <div key={i} className={styles.Card}>
          <h3 className={styles.CardTitle}>{card.title}</h3>
          <CardBody card={card} />
        </div>
      ))}
    </div>
  );
}

export type TextCardsProps_Export = TextCardsProps;
